/**
 * lazyPages centralizes all React.lazy declarations that used to live in App.tsx.
 * Keeping them in one place avoids circular imports and keeps route modules tidy.
 *
 * Every declaration goes through `lazyWithRetry` so a chunk that 404s after a
 * deploy (hashed filename changed / old chunk deleted from S3) triggers a
 * single guarded page reload instead of leaving the user on a blank screen.
 */
import { lazy } from 'react';
import { importWithChunkReload } from '../../utils/lazyRetry';

const lazyWithRetry: typeof lazy = (loader) => lazy(() => importWithChunkReload(loader));

// User Components
export const MemberLayout = lazyWithRetry(() => import('../../layout/MemberLayout'));
export const CorporateLayout = lazyWithRetry(() => import('../../layout/CorporateLayout'));
export const MemberHome = lazyWithRetry(() => import('../../components/member/MemberHome'));
export const MemberThriveOnboard = lazyWithRetry(
  () => import('../../components/member/MemberThriveOnboard')
);
export const MemberProfile = lazyWithRetry(() => import('../../components/member/MemberProfile'));
export const MemberBookTest = lazyWithRetry(() => import('../../components/member/MemberBookTest'));
export const MemberThriveCheck = lazyWithRetry(
  () => import('../../components/member/MemberThriveCheck')
);
export const MemberCognitiveAssessment = lazyWithRetry(
  () => import('../../components/member/MemberCognitiveAssessment')
);
export const MemberIntakeCall = lazyWithRetry(
  () => import('../../components/member/MemberIntakeCall')
);
export const MemberIntakeForm = lazyWithRetry(
  () => import('../../components/member/MemberIntakeForm')
);
export const MemberInCenterVisit = lazyWithRetry(
  () => import('../../components/member/MemberInCenterVisit')
);
export const MemberLongevityConsultation = lazyWithRetry(
  () => import('../../components/member/MemberLongevityConsultation')
);
export const MemberInCenterSteps = lazyWithRetry(
  () => import('../../components/member/MemberInCenterSteps')
);
export const MemberReports = lazyWithRetry(() => import('../../components/member/MemberReports'));
export const MemberClinicalNotes = lazyWithRetry(
  () => import('../../components/member/MemberClinicalNotes')
);
export const MemberSupport = lazyWithRetry(() => import('../../components/member/MemberSupport'));
export const MemberCheckout = lazyWithRetry(() => import('../../components/member/MemberCheckout'));
export const MemberPaymentStatus = lazyWithRetry(
  () => import('../../components/member/MemberPaymentStatus')
);
export const MemberTodo = lazyWithRetry(() => import('../../components/member/MemberTodo'));
export const MemberProduct = lazyWithRetry(() => import('../../components/member/MemberProducts'));
export const MemberSettings = lazyWithRetry(() => import('../../components/member/MemberSettings'));
export const MemberHealthData = lazyWithRetry(
  () => import('../../components/member/MemberMyHealthData')
);
export const MemberHealthSnapshot = lazyWithRetry(
  () => import('../../components/member/MemberHealthSnapshot')
);
export const MemberPathwayDetails = lazyWithRetry(
  () => import('../../components/member/MemberPathwayDetails')
);
export const MemberBiomarkerDetails = lazyWithRetry(
  () => import('../../components/member/MemberBiomarkerDetails')
);
export const MemberNotifications = lazyWithRetry(
  () => import('../../components/member/MemberNotifications')
);
export const NotificationCenter = lazyWithRetry(
  () => import('../../features/notifications/components/NotificationCenter')
);
export const MemberUserVisits = lazyWithRetry(
  () => import('../../components/member/MemberUserVisits')
);
export const MemberThriveKommune = lazyWithRetry(
  () => import('../../components/member/MemberThriveKommune')
);
export const MemberLabTest = lazyWithRetry(() => import('../../components/member/MemberLabTest'));
export const MemberBoxBreathe = lazyWithRetry(
  () => import('../../components/member/MemberBoxBreathe')
);
export const MemberMarketplace = lazyWithRetry(
  () => import('../../components/member/MemberMarketplace')
);
export const MemberMyCarts = lazyWithRetry(() => import('../../components/member/MemberMyCarts'));
export const MemberMyPurchases = lazyWithRetry(
  () => import('../../components/member/MemberMyPurchases')
);
export const MemberMemberships = lazyWithRetry(
  () => import('../../components/member/MemberMemberships')
);
export const ClientTicketsPage = lazyWithRetry(
  () => import('../../pages/tickets/ClientTicketsPage')
);
export const ClientTicketDetailPage = lazyWithRetry(
  () => import('../../pages/tickets/ClientTicketDetailPage')
);
export const MemberMyBenefits = lazyWithRetry(
  () => import('../../components/member/MemberMyBenefits')
);
export const ThyrocareRedemption = lazyWithRetry(
  () => import('../../components/member/entitlements/ThyrocareRedemption')
);
export const CenterCheckinRedemption = lazyWithRetry(
  () => import('../../components/member/entitlements/CenterCheckinRedemption')
);
export const MarketplaceManagement = lazyWithRetry(
  () => import('../../components/superclinic/MarketplaceManagement')
);
export const ServiceQueuePage = lazyWithRetry(
  () => import('../../components/superclinic/ServiceQueuePage')
);
export const MemberFamily = lazyWithRetry(
  () => import('../../components/member/family/FamilyDashboard')
);
export const MemberVideos = lazyWithRetry(() => import('../../components/member/MemberVideos'));

// Wearables Pages
export const WearableConnectionsPage = lazyWithRetry(
  () => import('../../components/wearables/pages/WearableConnectionsPage')
);
export const ConnectDevicePage = lazyWithRetry(
  () => import('../../components/wearables/pages/ConnectDevicePage')
);
export const OAuthCallbackPage = lazyWithRetry(
  () => import('../../components/wearables/pages/OAuthCallbackPage')
);
export const SleepListPage = lazyWithRetry(
  () => import('../../components/wearables/pages/SleepListPage')
);
export const SleepDetailPage = lazyWithRetry(
  () => import('../../components/wearables/pages/SleepDetailPage')
);
export const ActivityListPage = lazyWithRetry(
  () => import('../../components/wearables/pages/ActivityListPage')
);
export const ActivityDetailPage = lazyWithRetry(
  () => import('../../components/wearables/pages/ActivityDetailPage')
);
export const DailyListPage = lazyWithRetry(
  () => import('../../components/wearables/pages/DailyListPage')
);
export const DailyDetailPage = lazyWithRetry(
  () => import('../../components/wearables/pages/DailyDetailPage')
);
export const BodyListPage = lazyWithRetry(
  () => import('../../components/wearables/pages/BodyListPage')
);
export const PatientWearableDashboard = lazyWithRetry(
  () => import('../../components/wearables/pages/PatientWearableDashboard')
);
export const PatientWearableSleepPage = lazyWithRetry(
  () => import('../../components/wearables/pages/PatientWearableSleepPage')
);
export const PatientWearableActivityPage = lazyWithRetry(
  () => import('../../components/wearables/pages/PatientWearableActivityPage')
);
export const PatientWearableDailyPage = lazyWithRetry(
  () => import('../../components/wearables/pages/PatientWearableDailyPage')
);
export const AdminWearablesPage = lazyWithRetry(
  () => import('../../components/wearables/pages/AdminWearablesPage')
);

// Member Authentication Components
export const MemberLogin = lazyWithRetry(() => import('../../components/member/MemberLogin'));
export const MemberSignUp = lazyWithRetry(() => import('../../components/member/MemberSignUp'));
export const OldLoginPage = lazyWithRetry(() => import('../../components/member/OldLoginPage'));
export const MagicLinkLogin = lazyWithRetry(() => import('../../pages/auth/MagicLinkLogin'));
export const RequestMagicLink = lazyWithRetry(() => import('../../pages/auth/RequestMagicLink'));
export const MemberProgramSignup = lazyWithRetry(
  () => import('../../components/member/MemberProgramSignup')
);
export const ThriveEarlyCheckout = lazyWithRetry(
  () => import('../../components/member/ThriveEarlyCheckout')
);
export const ThriveEarlySignup = lazyWithRetry(
  () => import('../../components/member/ThriveEarlySignup')
);
export const GiftThriveEarly = lazyWithRetry(
  () => import('../../components/member/GiftThriveEarly')
);
export const ThriveCheckPublicPage = lazyWithRetry(
  () => import('../../pages/public/ThriveCheckPublicPage')
);

// Corporate Admin Components
export const CorporateDashboard = lazyWithRetry(
  () => import('../../pages/corporates/CorporateDashboard')
);
export const CorporateEmployees = lazyWithRetry(
  () => import('../../pages/corporates/CorporateEmployees')
);
export const CorporateManagePage = lazyWithRetry(
  () => import('../../pages/corporates/CorporateManagePage')
);

// Family Dashboard (detail page)
export const FamilyDashboard = lazyWithRetry(() => import('../../pages/families/FamilyDashboard'));

// Pod Dashboard
export const PodDashboard = lazyWithRetry(() => import('../../pages/pods/PodDashboard'));

// Tag Dashboard
export const TagDashboard = lazyWithRetry(() => import('../../pages/tags/TagDashboard'));

// Group Dashboard
export const GroupDashboard = lazyWithRetry(() => import('../../pages/groups/GroupDashboard'));

// Admin / Internal Components
export const UserProfilePage = lazyWithRetry(() => import('../../pages/users/UserProfilePage'));
export const CreateUserPage = lazyWithRetry(() => import('../../pages/users/CreateUserPage'));
export const ClientJourneyPage = lazyWithRetry(() => import('../../pages/users/ClientJourneyPage'));
export const NewUserProfilePage = lazyWithRetry(() =>
  import('../../pages/profiles/UserProfilePage').then((module) => ({
    default: module.default,
  }))
);
export const Hub = lazyWithRetry(() => import('../../components/superclinic/Hub'));
export const EarlyHubRosterPage = lazyWithRetry(
  () => import('../../pages/pmx-early-ops/EarlyHubRosterPage')
);
export const EarlyHubJourneyPage = lazyWithRetry(
  () => import('../../pages/pmx-early-ops/EarlyHubJourneyPage')
);
export const Appointments = lazyWithRetry(
  () => import('../../components/superclinic/Appointments')
);
export const PageRecordingsPage = lazyWithRetry(() => import('../../pages/PageRecordingsPage'));
export const LeadsPage = lazyWithRetry(() => import('../../pages/leads/LeadsPage'));
export const LeadEditorPage = lazyWithRetry(() => import('../../pages/leads/LeadEditorPage'));
export const CalendarView = lazyWithRetry(() => import('../../pages/CalendarView'));
export const Backoffice = lazyWithRetry(() => import('../../components/superclinic/BackOffice'));
export const CreateTask = lazyWithRetry(() => import('../../subpages/backoffice/CreateTask'));
export const CreateChecklist = lazyWithRetry(
  () => import('../../subpages/backoffice/CreateChecklist')
);
export const EditTask = lazyWithRetry(() => import('../../subpages/backoffice/EditTask'));
export const EditChecklist = lazyWithRetry(() => import('../../subpages/backoffice/EditChecklist'));
export const CreateProgram = lazyWithRetry(() => import('../../subpages/backoffice/CreateProgram'));
export const DoctorAssignmentBackoffice = lazyWithRetry(
  () => import('../../subpages/backoffice/DoctorAssignmentBackoffice')
);
export const CreateAddon = lazyWithRetry(() => import('../../subpages/backoffice/CreateAddon'));
export const CreateMembership = lazyWithRetry(
  () => import('../../subpages/backoffice/CreateMembership')
);
export const CreateTeam = lazyWithRetry(() => import('../../subpages/backoffice/CreateTeam'));
export const CitationDetailPage = lazyWithRetry(
  () => import('../../subpages/backoffice/CitationDetailPage')
);
export const Dashboard = lazyWithRetry(() =>
  import('../../pages/dashboard/Dashboard').then((module) => ({
    default: module.Dashboard,
  }))
);
export const UserManagement = lazyWithRetry(
  () => import('../../components/user-management/UserManagement')
);
export const IssueReports = lazyWithRetry(() => import('../../pages/IssueReports'));
export const ThriveDashboard = lazyWithRetry(
  () => import('../../components/superclinic/ThriveDashboard')
);
export const ConsultationDashboard = lazyWithRetry(
  () => import('../../components/superclinic/consultation-dashboard')
);
export const ThriveUserDashboard = lazyWithRetry(
  () => import('../../components/superclinic/ThriveUserDashboard')
);
export const AppDashboard = lazyWithRetry(
  () => import('../../components/superclinic/AppDashboard')
);
export const AppUserDetail = lazyWithRetry(
  () => import('../../components/superclinic/AppUserDetail')
);
export const PodDetails = lazyWithRetry(() =>
  import('../../components/user-management/pods/PodDetails').then((module) => ({
    default: module.PodDetails,
  }))
);
export const ClientDiscovery = lazyWithRetry(() =>
  import('../../components/superclinic/ClientDiscovery').then((module) => ({
    default: module.ClientDiscovery,
  }))
);
export const ClientIVTherapy = lazyWithRetry(
  () => import('../../components/superclinic/ClientIVTherapy')
);
export const ClientHBOT = lazyWithRetry(() => import('../../components/superclinic/ClientHBOT'));
export const ClientPET = lazyWithRetry(() => import('../../components/superclinic/ClientPET'));
export const ClientRedLightTherapy = lazyWithRetry(
  () => import('../../components/superclinic/ClientRedLightTherapy')
);
export const ClientFIR = lazyWithRetry(() => import('../../components/superclinic/ClientFIR'));
export const ClientPsychotherapy = lazyWithRetry(
  () => import('../../components/superclinic/ClientPsychotherapy')
);
export const ClientDetailsPage = lazyWithRetry(() =>
  import('../../components/superclinic/ClientDetailsPage').then((module) => ({
    default: module.ClientDetailsPage,
  }))
);
export const DesignPlayground = lazyWithRetry(() =>
  import('../../components/superclinic/DesignPlayground').then((module) => ({
    default: module.DesignPlayground,
  }))
);
export const Studio = lazyWithRetry(() =>
  import('../../components/superclinic/studio/Studio').then((module) => ({
    default: module.default,
  }))
);
export const Builder = lazyWithRetry(() =>
  import('../../components/builder/BuilderPage').then((module) => ({
    default: module.default,
  }))
);
export const ThriveReport = lazyWithRetry(() =>
  import('../../components/superclinic/client-details/thrive-intelligence/ThriveReport').then(
    (module) => ({
      default: module.ThriveReport,
    })
  )
);
export const ReportsCompare = lazyWithRetry(() => import('../../pages/ReportsCompare'));
export const ReportDataEditorPage = lazyWithRetry(() =>
  import('../../components/superclinic/external_reports/ReportDataEditorPage').then((module) => ({
    default: module.ReportDataEditorPage,
  }))
);
export const RoadmapLongFormView = lazyWithRetry(() =>
  import('../../components/superclinic/client-details/roadmap/LongFormView').then((module) => ({
    default: module.LongFormView,
  }))
);
export const CompareFullPage = lazyWithRetry(() =>
  import('../../components/superclinic/client-details/roadmap/components/CompareFullPage').then(
    (module) => ({
      default: module.default,
    })
  )
);

// Nutrition Pages
export const NutritionDashboard = lazyWithRetry(() =>
  import('../../pages/nutrition/NutritionDashboard').then((module) => ({
    default: module.NutritionDashboard,
  }))
);
export const NutritionPlannerHome = lazyWithRetry(() =>
  import('../../pages/nutrition/NutritionPlannerHome').then((module) => ({
    default: module.NutritionPlannerHome,
  }))
);
export const FoodCatalogPage = lazyWithRetry(() =>
  import('../../pages/nutrition/FoodCatalogPage').then((module) => ({
    default: module.FoodCatalogPage,
  }))
);
export const RecipesPage = lazyWithRetry(() =>
  import('../../pages/nutrition/RecipesPage').then((module) => ({
    default: module.RecipesPage ?? module.default,
  }))
);
export const RecipeRecommendationsPage = lazyWithRetry(() =>
  import('../../pages/nutrition/RecipeRecommendationsPage').then((module) => ({
    default: module.RecipeRecommendationsPage,
  }))
);
export const RecipeBuilderPage = lazyWithRetry(() =>
  import('../../pages/nutrition/RecipeBuilderPage').then((module) => ({
    default: module.RecipeBuilderPage ?? module.default,
  }))
);
export const GuidancePage = lazyWithRetry(() =>
  import('../../pages/nutrition/GuidancePage').then((module) => ({
    default: module.GuidancePage,
  }))
);
export const MealPlansPage = lazyWithRetry(() =>
  import('../../pages/nutrition/MealPlansPage').then((module) => ({
    default: module.MealPlansPage,
  }))
);
export const MealPlanBuilderPage = lazyWithRetry(() =>
  import('../../pages/nutrition/MealPlanBuilderPage').then((module) => ({
    default: module.MealPlanBuilderPage,
  }))
);
export const TagsPage = lazyWithRetry(() =>
  import('../../pages/nutrition/TagsPage').then((module) => ({
    default: module.TagsPage,
  }))
);
export const PreferencesPage = lazyWithRetry(() =>
  import('../../pages/nutrition/PreferencesPage').then((module) => ({
    default: module.PreferencesPage,
  }))
);
export const ConditionRulesPage = lazyWithRetry(() =>
  import('../../pages/nutrition/ConditionRulesPage').then((module) => ({
    default: module.ConditionRulesPage,
  }))
);
export const ConditionGuidelinesPage = lazyWithRetry(() =>
  import('../../pages/nutrition/ConditionGuidelinesPage').then((module) => ({
    default: module.ConditionGuidelinesPage,
  }))
);
export const RecipeBanksPage = lazyWithRetry(() =>
  import('../../pages/nutrition/RecipeBanksPage').then((module) => ({
    default: module.RecipeBanksPage,
  }))
);
export const RulesPage = lazyWithRetry(() =>
  import('../../pages/nutrition/RulesPage').then((module) => ({
    default: module.RulesPage,
  }))
);
export const RuleSetsPage = lazyWithRetry(() =>
  import('../../pages/nutrition/RuleSetsPage').then((module) => ({
    default: module.RuleSetsPage,
  }))
);
export const IntakeLoggingPage = lazyWithRetry(() =>
  import('../../pages/nutrition/IntakeLoggingPage').then((module) => ({
    default: module.IntakeLoggingPage,
  }))
);
export const OutcomesPage = lazyWithRetry(() =>
  import('../../pages/nutrition/OutcomesPage').then((module) => ({
    default: module.OutcomesPage,
  }))
);
export const TemplatesPage = lazyWithRetry(() =>
  import('../../pages/nutrition/TemplatesPage').then((module) => ({
    default: module.TemplatesPage,
  }))
);
export const TemplateBuilderPage = lazyWithRetry(() =>
  import('../../pages/nutrition/TemplateBuilderPage').then((module) => ({
    default: module.TemplateBuilderPage,
  }))
);
export const NutritionPlanBuilderPage = lazyWithRetry(() =>
  import('../../pages/nutrition/NutritionPlanBuilderPage').then((module) => ({
    default: module.NutritionPlanBuilderPage,
  }))
);

// What's New Pages
export const WhatsNewPage = lazyWithRetry(() =>
  import('../../pages/whats-new/WhatsNewPage').then((module) => ({
    default: module.WhatsNewPage,
  }))
);
export const AddReleasePage = lazyWithRetry(() =>
  import('../../pages/whats-new/AddReleasePage').then((module) => ({
    default: module.AddReleasePage,
  }))
);
export const EditReleasePage = lazyWithRetry(() =>
  import('../../pages/whats-new/EditReleasePage').then((module) => ({
    default: module.EditReleasePage,
  }))
);

// Knowledge Base Pages
export const KnowledgeBasePage = lazyWithRetry(() =>
  import('../../pages/knowledge-base/KnowledgeBasePage').then((module) => ({
    default: module.KnowledgeBasePage,
  }))
);
export const AddArticlePage = lazyWithRetry(() =>
  import('../../pages/knowledge-base/AddArticlePage').then((module) => ({
    default: module.AddArticlePage,
  }))
);
export const EditArticlePage = lazyWithRetry(() =>
  import('../../pages/knowledge-base/EditArticlePage').then((module) => ({
    default: module.EditArticlePage,
  }))
);
export const ArticleViewPage = lazyWithRetry(() =>
  import('../../pages/knowledge-base/ArticleViewPage').then((module) => ({
    default: module.ArticleViewPage,
  }))
);
export const SOPPage = lazyWithRetry(() =>
  import('../../pages/knowledge-base/SOPPage').then((module) => ({
    default: module.SOPPage,
  }))
);

// Daily Intake Log Pages
export const DailyCheckinPage = lazyWithRetry(() =>
  import('../../pages/clinical/daily-intake/DailyCheckinPage').then((module) => ({
    default: module.DailyCheckinPage,
  }))
);
export const DailyCheckinHistoryPage = lazyWithRetry(() =>
  import('../../pages/clinical/daily-intake/DailyCheckinHistoryPage').then((module) => ({
    default: module.DailyCheckinHistoryPage,
  }))
);
export const DailyCheckinDashboardPage = lazyWithRetry(() =>
  import('../../pages/clinical/daily-intake/DailyCheckinDashboardPage').then((module) => ({
    default: module.DailyCheckinDashboardPage,
  }))
);
export const DailyCheckinSettingsPage = lazyWithRetry(() =>
  import('../../pages/clinical/daily-intake/DailyCheckinSettingsPage').then((module) => ({
    default: module.DailyCheckinSettingsPage,
  }))
);
export const PatientDailyLogsPage = lazyWithRetry(() =>
  import('../../components/clinical/daily-intake/provider/PatientDailyLogsPage').then((module) => ({
    default: module.PatientDailyLogsPage,
  }))
);

// Reference Data Pages
export const BiomarkerCodeMappingPage = lazyWithRetry(
  () => import('../../pages/reference-data/BiomarkerCodeMappingPage')
);
export const PromptActionsPage = lazyWithRetry(
  () => import('../../pages/reference-data/PromptActionsPage')
);

// Prompt Management Pages
export const PromptEditorPage = lazyWithRetry(
  () => import('../../pages/prompt-management/PromptEditorPage')
);
export const PromptPreviewPage = lazyWithRetry(
  () => import('../../pages/prompt-management/PromptPreviewPage')
);

// Chat Pages
export const HealthChatPage = lazyWithRetry(() =>
  import('../../components/member/chat/HealthChatPage').then((module) => ({
    default: module.HealthChatPage,
  }))
);
export const ChatFullPage = lazyWithRetry(() =>
  import('../../components/member/chat/ChatFullPage').then((module) => ({
    default: module.ChatFullPage,
  }))
);
export const ChatDemoPage = lazyWithRetry(() =>
  import('../../components/member/chat/ChatDemoPage').then((module) => ({
    default: module.ChatDemoPage,
  }))
);
export const PlatformChatPage = lazyWithRetry(() =>
  import('../../components/member/chat/PlatformChatPage').then((module) => ({
    default: module.PlatformChatPage,
  }))
);
export const AlgorithmsTourPage = lazyWithRetry(() =>
  import('../../pages/algorithms-tour/AlgorithmsTourPage').then((module) => ({
    default: module.AlgorithmsTourPage,
  }))
);

// Product ideas / roadmap board (shared between staff and members)
export const IdeasPage = lazyWithRetry(() => import('../../pages/ideas/IdeasPage'));

// User UI preferences (quick links + custom homepage)
export const UiPreferencesPage = lazyWithRetry(
  () => import('../../pages/preferences/UiPreferencesPage')
);

// Client Prep — doctor pre-consultation aggregator
export const ClientPrepPage = lazyWithRetry(() =>
  import('../../components/superclinic/client-prep/ClientPrepPage').then((module) => ({
    default: module.ClientPrepPage,
  }))
);

export const MemberPresentationPage = lazyWithRetry(() =>
  import('../../components/superclinic/consultation/components/presentation/MemberPresentationPage').then(
    (module) => ({
      default: module.MemberPresentationPage,
    })
  )
);
